import clsx from 'clsx';
import type { ButtonHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'danger' | 'dangerOutlined' | 'neutral' | 'outlined' | 'ghost';
  size?: 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
};

const buttonVariants = {
  primary: 'text-white bg-sky-800 hover:bg-sky-900 focus:ring-sky-500 font-bold px-6',
  danger: 'text-white bg-red-600 hover:bg-red-700 focus:ring-red-500 font-bold px-6',
  dangerOutlined:
    'text-red-700 bg-white hover:bg-red-50 border border-neutral-300 focus:ring-red-500 font-bold px-4',
  neutral: 'bg-neutral-200 hover:bg-neutral-300 focus:ring-neutral-200 font-bold px-6',
  ghost: 'hover:bg-neutral-100 px-1.5 border border-transparent',
  outlined:
    'text-sky-800 bg-white hover:text-white hover:bg-sky-800 border-sky-800 border focus:ring-sky-500 px-4',
};

const buttonBaseStyles =
  'flex justify-center items-center gap-1 duration-300 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap';

const buttonSizes = {
  md: 'py-1',
  lg: 'h-12',
};

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled,
  className = '',
  ...props
}: ButtonProps) => {
  return (
    <button
      className={clsx(
        buttonBaseStyles,
        buttonSizes[size],
        buttonVariants[variant],
        fullWidth && 'w-full',
        className,
      )}
      disabled={loading || disabled}
      {...props}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" aria-label="button-loading">
          <title>button loading</title>
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};

type LinkButtonProps = LinkProps & {
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  fullWidth?: boolean;
};

export const LinkButton = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
  ...props
}: LinkButtonProps) => (
  <Link
    className={clsx(
      buttonBaseStyles,
      buttonSizes[size],
      buttonVariants[variant],
      fullWidth && 'w-full',
      className,
    )}
    {...props}
  />
);
